import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import DashboardLayout from '../components/DashboardLayout';
import { XMarkIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

interface BannerFormData {
  imageUrl: string;
  linkType: 'external' | 'internal';
  externalUrl: string;
  internalRoute: string;
  recipeId: string; // when internalRoute is /recipe
  internalPayloadJson: string;
  order: number;
  active: boolean;
  /** Optional: shown on app banner (bottom, white bold) */
  title: string;
  /** Optional: lighter white text below title */
  description: string;
}

// Match app banner carousel: card is 96% viewport × 184px (e.g. ~346×184). 2× for retina → ~692×368; use 690×368.
const BANNER_WIDTH = 690;
const BANNER_HEIGHT = 368;
const BANNER_RESOLUTION_LABEL = `${BANNER_WIDTH}×${BANNER_HEIGHT}`;
const RESOLUTION_GUIDANCE = `Matches app display (96% width × 184px). ${BANNER_RESOLUTION_LABEL}px. Resized only if dimensions don't match. Use JPG or PNG.`;

/** Static app routes for banners; avoids typos and invalid routes. */
const APP_ROUTES = [
  { value: '/search', label: 'Search' },
  { value: '/community', label: 'Community' },
  { value: '/home', label: 'Home' },
  { value: '/pantry', label: 'Pantry' },
  { value: '/smart-planner', label: 'Smart planner' },
  { value: '/profile', label: 'Profile' },
  { value: '/favorites', label: 'Favorites' },
  { value: '/cart', label: 'Cart' },
  { value: '/orders', label: 'Orders' },
  { value: '/notifications', label: 'Notifications' },
  { value: '/wallet', label: 'Wallet' },
  { value: '/recipe', label: 'Recipe (specific)' },
] as const;

export default function BannerForm() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const [formData, setFormData] = useState<BannerFormData>({
    imageUrl: '',
    linkType: 'internal',
    externalUrl: '',
    internalRoute: '/search',
    recipeId: '',
    internalPayloadJson: '{"query": ""}',
    order: 0,
    active: true,
    title: '',
    description: '',
  });

  useEffect(() => {
    if (isEditing) loadBanner();
  }, [id]);

  const loadBanner = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await api.get(`/admin/banners/${id}`);
      const b = response.data.data;
      const route = b.internalRoute || '/search';
      const isRecipe = route.startsWith('/recipe/');
      setFormData({
        imageUrl: b.imageUrl || '',
        linkType: b.linkType || 'internal',
        externalUrl: b.externalUrl || '',
        internalRoute: isRecipe ? '/recipe' : (APP_ROUTES.some((r) => r.value === route) ? route : '/search'),
        recipeId: isRecipe ? route.replace(/^\/recipe\//, '') : '',
        internalPayloadJson: b.internalPayload ? JSON.stringify(b.internalPayload, null, 2) : '{}',
        order: b.order ?? 0,
        active: b.active !== false,
        title: typeof b.title === 'string' ? b.title : '',
        description: typeof b.description === 'string' ? b.description : '',
      });
    } catch (error: any) {
      console.error('Error loading banner:', error);
      alert(error.response?.data?.message || t('banners.failedToLoad'));
      navigate('/banners');
    } finally {
      setLoading(false);
    }
  };

  const uploadImage = async (file: File): Promise<string> => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('folder', 'banners');
    fd.append('type', 'image');
    fd.append('width', String(BANNER_WIDTH));
    fd.append('height', String(BANNER_HEIGHT));
    setUploading(t('bannerForm.uploading', { name: file.name }));
    try {
      const response = await api.post('/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploading(null);
      const url = response.data?.data?.url;
      if (url) return url;
      throw new Error('Upload failed');
    } catch (err: any) {
      setUploading(null);
      throw new Error(err.response?.data?.message || 'Upload failed');
    }
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const url = await uploadImage(file);
      setFormData((prev) => ({ ...prev, imageUrl: url }));
    } catch (msg: any) {
      alert(msg?.message || 'Upload failed');
    }
    e.target.value = '';
  };

  const parsePayload = (): Record<string, unknown> | null => {
    const s = formData.internalPayloadJson?.trim() || '{}';
    try {
      return JSON.parse(s) as Record<string, unknown>;
    } catch {
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = formData.linkType === 'internal' ? parsePayload() : undefined;
    if (formData.linkType === 'internal' && payload === null) {
      alert(t('bannerForm.invalidJson'));
      return;
    }
    const body = {
      imageUrl: formData.imageUrl,
      linkType: formData.linkType,
      order: formData.order,
      active: formData.active,
      title: formData.title.trim(),
      description: formData.description.trim(),
      ...(formData.linkType === 'external' && formData.externalUrl && { externalUrl: formData.externalUrl }),
      ...(formData.linkType === 'internal' && {
        internalRoute: formData.internalRoute === '/recipe' && formData.recipeId
          ? `/recipe/${formData.recipeId.trim()}`
          : (formData.internalRoute || '/search'),
        internalPayload: payload || {},
      }),
    };
    try {
      setLoading(true);
      if (isEditing && id) {
        await api.put(`/admin/banners/${id}`, body);
      } else {
        await api.post('/admin/banners', body);
      }
      navigate('/banners');
    } catch (error: any) {
      alert(error.response?.data?.message || t('bannerForm.failedToSave'));
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditing) {
    return (
      <DashboardLayout title={t('common.loading')}>
        <div className="flex justify-center items-center min-h-[400px]">{t('bannerForm.loading')}</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title={isEditing ? t('bannerForm.editBanner') : t('bannerForm.createBanner')}
      subtitle={t('bannerForm.subtitleHome')}
    >
      <div className="max-w-2xl mx-auto">
        {/* Resolution guidance */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex gap-3">
          <InformationCircleIcon className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-blue-900">{t('bannerForm.imageResolution')}</p>
            <p className="text-sm text-blue-800 mt-0.5">{RESOLUTION_GUIDANCE}</p>
          </div>
        </div>

        {uploading && (
          <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg text-sm text-orange-800">
            {uploading}
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6 space-y-6">
          {/* Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('bannerForm.bannerImageRequired')}</label>
            {formData.imageUrl ? (
              <div className="relative inline-block">
                <img
                  src={formData.imageUrl}
                  alt="Banner"
                  className="max-w-full h-32 object-cover rounded-lg border border-gray-200"
                />
                <button
                  type="button"
                  onClick={() => setFormData((p) => ({ ...p, imageUrl: '' }))}
                  className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600"
                  title={t('bannerForm.remove')}
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
            ) : null}
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="mt-2 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-orange file:text-white hover:file:bg-orange-600"
            />
          </div>

          {/* Optional caption (app home banner) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('bannerForm.bannerTitleOptional')}</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData((p) => ({ ...p, title: e.target.value }))}
              placeholder={t('bannerForm.bannerTitlePlaceholder')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-orange focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('bannerForm.bannerDescriptionOptional')}</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
              rows={2}
              placeholder={t('bannerForm.bannerDescriptionPlaceholder')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-orange focus:border-transparent"
            />
            <p className="mt-1 text-xs text-gray-500">{t('bannerForm.bannerCaptionHint')}</p>
          </div>

          {/* Link type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('bannerForm.linkType')}</label>
            <select
              value={formData.linkType}
              onChange={(e) => setFormData((p) => ({ ...p, linkType: e.target.value as 'external' | 'internal' }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-orange focus:border-transparent"
            >
              <option value="internal">{t('bannerForm.inAppOption')}</option>
              <option value="external">{t('bannerForm.externalOption')}</option>
            </select>
          </div>

          {formData.linkType === 'external' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('bannerForm.externalUrlRequired')}</label>
              <input
                type="url"
                value={formData.externalUrl}
                onChange={(e) => setFormData((p) => ({ ...p, externalUrl: e.target.value }))}
                placeholder="https://..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-orange focus:border-transparent"
              />
            </div>
          )}

          {formData.linkType === 'internal' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('bannerForm.appRoute')}</label>
                <select
                  value={formData.internalRoute}
                  onChange={(e) => setFormData((p) => ({ ...p, internalRoute: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                >
                  {APP_ROUTES.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>
              {formData.internalRoute === '/recipe' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('bannerForm.recipeIdRequired')}</label>
                  <input
                    type="text"
                    value={formData.recipeId}
                    onChange={(e) => setFormData((p) => ({ ...p, recipeId: e.target.value }))}
                    placeholder={t('bannerForm.recipeIdPlaceholder')}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('bannerForm.payloadJson')}</label>
                <textarea
                  value={formData.internalPayloadJson}
                  onChange={(e) => setFormData((p) => ({ ...p, internalPayloadJson: e.target.value }))}
                  rows={3}
                  placeholder='{"query": "pasta"}'
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 font-mono text-sm focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500">{t('bannerForm.payloadHint')}</p>
              </div>
            </>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('bannerForm.order')}</label>
              <input
                type="number"
                min={0}
                value={formData.order}
                onChange={(e) => setFormData((p) => ({ ...p, order: parseInt(e.target.value, 10) || 0 }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-orange focus:border-transparent"
              />
            </div>
            <div className="flex items-center gap-2 pt-8">
              <input
                type="checkbox"
                id="active"
                checked={formData.active}
                onChange={(e) => setFormData((p) => ({ ...p, active: e.target.checked }))}
                className="rounded border-gray-300 text-primary-orange focus:ring-primary-orange"
              />
              <label htmlFor="active" className="text-sm font-medium text-gray-700">{t('bannerForm.active')}</label>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={!formData.imageUrl || loading}
              className="px-4 py-2 bg-primary-orange text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('bannerForm.saving') : isEditing ? t('bannerForm.updateBanner') : t('bannerForm.createBannerBtn')}
            </button>
            <button
              type="button"
              onClick={() => navigate('/banners')}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              {t('common.cancel')}
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}
